import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Plus, Minus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, UserSearch, ChartLine } from "lucide-react";

/* ======= Assets ======= */
import HeroBG from "@/assets/Frame.png";
import DashboardImage from "@/assets/Dashboard.svg";
import CandidateImage from "@/assets/Candidates.svg";
import ClientImage from "@/assets/Clients.svg";
import SkillsImage from "@/assets/Consolidated_Report.svg";
import { motion, Variants } from "framer-motion";

const GradientIcon = ({ icon: Icon }) => {
  const gradientId = "icon-gradient";

  return (
    <svg width="0" height="0">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFD600" />
          <stop offset="100%" stopColor="#FF007A" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const slideInLeft: Variants = {
  hidden: { opacity: 0, x: -100 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 1.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const slideInRight: Variants = {
  hidden: { opacity: 0, x: 100 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 1.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const slideInTop: Variants = {
  hidden: { opacity: 0, y: -80 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const testimonialContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.18,
      delayChildren: 0.2,
    },
  },
};

const testimonialItem: Variants = {
  hidden: { opacity: 0, y: 50, rotateX: 8, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    filter: "blur(0px)",
    transition: { duration: 1.0, ease: [0.16, 1, 0.3, 1] },
  },
};

const ModernHiring = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question:
        "Can Smartborn manage candidates, jobs, and vendors in one place?",
      answer:
        "Smartborn brings candidate tracking, job management, vendor coordination, and hiring pipelines together in a single integrated platform.",
    },
    {
      question:
        "Does Smartborn support interview scheduling and activity tracking?",
      answer:
        "Yes, Smartborn enables seamless interview scheduling and centralized activity tracking. Recruiters can manage follow-ups, feedback, and candidate interactions in one organized system.",
    },
    {
      question:
        "How does Smartborn provide recruitment analytics and reporting?",
      answer:
        "Smartborn offers real-time dashboards and detailed recruitment reports. Teams can track performance metrics and make informed hiring decisions with actionable insights.",
    },
    {
      question: "Is Smartborn suitable for enterprises and staffing firms?",
      answer:
        "Yes, Smartborn is built to support both growing teams and large recruitment operations. Its scalable workflows and performance tracking make it ideal for staffing firms and enterprises.",
    },
  ];

  return (
    <div className="bg-background">
      <Navigation />

      {/* ================= HERO ================= */}
      <section
        className="pt-28 pb-24 bg-black text-white text-center relative"
        style={{
          backgroundImage: `url(${HeroBG})`,
          backgroundSize: "cover",
          backgroundPosition: "left",
        }}
      >
        <div className="container mx-auto px-6 max-w-3xl">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="text-orange-500">The Platform for</span>
            <br />
            <span className="text-orange-500">Modern Hiring</span>
          </h1>

          <p className="text-white sm:text-lg lg:text-xl mb-8 max-w-2xl mx-auto">
            Smartborn helps teams manage hiring, track <br></br> candidates and
            vendors, and analyze recruitment <br></br> performance on one
            powerful recruitment platform.
          </p>

          <Button
            size="lg"
            asChild
            className="bg-orange-500 hover:bg-orange-600"
          >
            <Link to="/contact">
              Request Demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      {/* ================= DASHBOARD SECTION ================= */}
      <section className="py-28 bg-gradient-to-b from-[#FBE3CF] to-[#F2994A]">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          {/* TEXT */}
          <div className="max-w-xl">
            <motion.div
              className="text-center lg:text-left"
              variants={slideInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            >
              <p className="text-lg font-medium text-black/70 mb-3">
                Recruitment Dashboard
              </p>

              <h2 className="text-4xl font-bold mb-6 leading-tight">
                See your hiring <br /> at a glance.
              </h2>

              <p className="text-black/70 text-lg leading-relaxed">
                Track jobs, candidates, submissions, interviews, and closures
                from a single real-time dashboard. Gain instant visibility into
                hiring performance to make faster, data-driven decisions.
              </p>
            </motion.div>
          </div>

          {/* IMAGE */}
          <div className="relative">
            <motion.div
              className="text-center lg:text-left"
              variants={slideInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            >
              <img
                src={DashboardImage}
                alt="Dashboard"
                className="rounded-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= SOLUTIONS ================= */}
      <section className="py-20 bg-gray-100 text-center">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12">
            Smart hiring solutions for every team
          </h2>

          <motion.div
            className="grid md:grid-cols-3 gap-8"
            variants={testimonialContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
          >
            {[
              {
                icon: ClipboardList,
                title: "For Staffing Agencies",
                desc: "Deliver faster, better-quality placements and accelerate client hiring outcomes.",
              },
              {
                icon: UserSearch,
                title: "For Corporate HR Teams",
                desc: "HR digital toolkit, more efficiency and strengthen internal hiring performance.",
              },
              {
                icon: ChartLine,
                title: "For Growing Companies",
                desc: "Build stronger teams with better tracking and collaboration tools.",
              },
            ].map((item, i) => {
              const Icon = item.icon;

              return (
                <motion.div
                  key={i}
                  variants={testimonialItem}
                  className="bg-[#FBE3CF] rounded-xl p-8 shadow-md text-left"
                >
                  <div className="mb-4 relative">
                    <svg width="0" height="0">
                      <defs>
                        <linearGradient
                          id="cardIconGradient"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="#FFD600" />
                          <stop offset="100%" stopColor="#FF007A" />
                        </linearGradient>
                      </defs>
                    </svg>

                    <Icon
                      className="w-8 h-8"
                      stroke="url(#cardIconGradient)"
                      strokeWidth={2}
                    />
                  </div>

                  <h4 className="font-semibold mb-3">{item.title}</h4>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ================= STATS ================= */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-yellow-400 text-center text-black">
        <h3 className="text-3xl font-bold mb-24">
          Trusted by teams managing <br></br> modern workforces
        </h3>
        <div className="container mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { number: "10+", label: "companies" },
            { number: "120+", label: "employees" },
            { number: "250+", label: "candidates" },
            { number: "99%", label: "satisfaction" },
          ].map((item, i) => (
            <div key={i}>
              <motion.div
                className="text-center lg:text-left"
                variants={slideInTop}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.4 }}
              >
                <div className="text-3xl text-center font-bold">
                  {item.number}
                </div>
                <div className="text-sm text-center">{item.label}</div>
              </motion.div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= FEATURE SECTIONS ================= */}
      {/* <section className="py-20 bg-white"> */}
      <section className="py-28 bg-gradient-to-b from-white to-black/90">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* TEXT */}
            <div className="max-w-lg">
              <motion.div
                className="text-center lg:text-left"
                variants={slideInRight}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.4 }}
              >
                <p className="text-lg font-medium text-black/60 mb-4">
                  Candidate Hub
                </p>

                <h3 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                  Track candidate easily.
                </h3>

                <p className="text-white/70 text-lg leading-relaxed">
                  Search, filter, and manage candidates in one organized
                  workspace. Review profiles, matches, and progress to move the
                  right people forward.
                </p>
              </motion.div>
            </div>

            {/* IMAGE */}
            <div className="flex justify-center lg:justify-end">
              <motion.div
                className="text-center lg:text-left"
                variants={slideInLeft}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.4 }}
              >
                <img
                  src={CandidateImage}
                  alt="Candidates"
                  className="w-full max-w-2xl rounded-2xl "
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Management */}
      {/* <section className="py-20 bg-gradient-to-b from-orange-100 to-orange-400">
        <div className="container mx-auto px-6 space-y-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm mb-2">Client Management</p>
              <h3 className="text-3xl font-bold mb-4">
                Organize hiring partnerships.
              </h3>
              <p className="text-muted-foreground">
                Centralize client information and ongoing recruitment activity
                in a single workspace.Monitor job pipelines and hiring status as
                they progress through each stage.
              </p>
            </div>
            <img src={ClientImage} className="rounded-xl shadow-xl" />
          </div>
        </div>
          </section> */}

      <section className="py-28 bg-gradient-to-b from-[#FBE3CF] to-[#F2994A]">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-20 items-center">
          {/* IMAGE LEFT */}
          <div className="order-2 lg:order-1">
            <motion.div
              className="text-center lg:text-left"
              variants={slideInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            >
              <img
                src={ClientImage}
                alt="Clients"
                className="rounded-2xl w-full"
              />
            </motion.div>
          </div>

          {/* TEXT RIGHT */}
          <div className="max-w-xl order-1 lg:order-2">
            <motion.div
              className="text-center lg:text-left"
              variants={slideInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            >
              <p className="text-lg font-medium text-black/70 mb-3">
                Client Management
              </p>

              <h3 className="text-4xl font-bold mb-6">
                Organize hiring partnerships.
              </h3>

              <p className="text-black/70 text-lg leading-relaxed">
                Centralize client information and ongoing recruitment activity
                in a single workspace. Monitor job pipelines and hiring status
                as they progress through each stage.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Skills Intelligence */}
      {/* <section className="py-20 bg-gradient-to-b from-white to-black">
        <div className="container mx-auto px-6 space-y-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm mb-2">Skills Intelligence</p>
              <h3 className="text-3xl font-bold mb-4">
                Assess workforce skill demands.
              </h3>
              <p className="text-muted-foreground">
                Analyze how skills are distributed, where gaps exist, and which
                skills lead to hires. Use visual insights to align hiring
                strategy with real market demand.
              </p>
            </div>
            <img src={SkillsImage} className="rounded-xl shadow-xl" />
          </div>
        </div>
      </section> */}

      <section className="py-28 bg-gradient-to-b from-white to-black">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-20 items-center">
          <div className="max-w-xl">
            <motion.div
              className="text-center lg:text-left"
              variants={slideInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            >
              <p className="text-lg font-medium text-black/60 mb-3">
                Skills Intelligence
              </p>

              <h3 className="text-4xl font-bold mb-6">
                Assess workforce skill demands.
              </h3>

              <p className="text-white/70 text-lg leading-relaxed">
                Analyze how skills are distributed, where gaps exist, and which
                skills lead to hires. Use visual insights to align hiring
                strategy with real market demand.
              </p>
            </motion.div>
          </div>

          <div>
            <motion.div
              className="text-center lg:text-left"
              variants={slideInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            >
              <img
                src={SkillsImage}
                alt="Skills"
                className="rounded-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      {/* <section className="py-20 bg-gray-100">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 className="text-3xl font-bold mb-10">
            Frequently Asked Questions
          </h2>

          {faqs.map((item, index) => {
            const isOpen = openIndex === index;

            return (
              <div
                key={index}
                className="border-b py-6 cursor-pointer"
                onClick={() => setOpenIndex(isOpen ? null : index)}
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium">{item.question}</span>
                  {isOpen ? <Minus /> : <Plus />}
                </div>

                {isOpen && (
                  <p className="mt-4 text-sm text-muted-foreground">
                    {item.answer}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section> */}

      <section className="py-28 bg-[#F3F3F3]">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-4xl font-bold mb-12">
            Frequently Asked Questions
          </h2>

          <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
            {faqs.map((item, index) => {
              const isOpen = openIndex === index;
              const isFirst = index === 0;
              const isLast = index === faqs.length - 1;

              return (
                <div
                  key={index}
                  className={`
              cursor-pointer transition-all
              ${!isLast ? "border-b border-gray-200" : ""}
              ${isOpen ? "bg-[#FBE3CF]" : "bg-white"}
            `}
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                >
                  <div className="p-6 flex justify-between items-center">
                    <span className="font-semibold text-lg">
                      {item.question}
                    </span>
                    {isOpen ? <Minus /> : <Plus />}
                  </div>

                  <div
                    className={`
    grid transition-all duration-500 ease-in-out
    ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}
  `}
                  >
                    <div className="overflow-hidden">
                      <div className="px-6 pb-6">
                        <p className="text-black/70 leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ModernHiring;
